import {Component, Input, OnInit} from '@angular/core';
import {DatePipe, NgForOf, NgIf} from "@angular/common";
import {NgbActiveModal} from "@ng-bootstrap/ng-bootstrap";
import {ToastrService} from "ngx-toastr";
import {SpinnerComponent} from "@shared/components/spinner/spinner.component";
import {OtpModeService} from "@shared/api/services/v2/otp-mode.service";
import {OtpModeEnum, OtpModeHistoryEntry} from "@shared/api/models/query/otp-mode-history.model";

/**
 * UNIVAPLAT-2994: admin-dashboard modal showing a user's full OTP mode
 * change history (`GET /api/v2/admin/users/{userId}/otp-mode/history` via
 * {@link OtpModeService.listHistory}, newest-first per backend contract
 * UNIVAPLAT-2977).
 *
 * Modelled on `LicenseHistoryModal` (UNIVAPLAT-2774): same title/user header
 * + loading/error/empty/success states. Unlike the license history modal,
 * there is no edit affordance here — the OTP mode history is a read-only
 * audit trail, nothing in a row is mutable from this view.
 *
 * Table columns, in order (ticket-specified, UNIVAPLAT-2994 description):
 * Date, Prev. status, New status, Static OTP, Reason. The Static OTP cell
 * shows `-` when the entry carries no code (`staticCode === null`, e.g. a
 * switch back to dynamic). Dates render via Angular's DatePipe in the same
 * `dd/MM/yyyy HH:mm` format the license history and admin list already use.
 *
 * Security constraint (Epic UNIVAPLAT-2976 scope): this modal is the ONLY
 * place in the portal that may display a static code. It must never be
 * added to the row table, a tooltip, an export, or any analytics payload.
 * `entries` lives only on this component instance — {@link OtpModeService}
 * never caches the history at the service level (its class-level doc) and
 * the page never holds a copy — so the payload is discarded by Angular's
 * normal component teardown the moment the modal closes.
 */
@Component({
    selector: 'cdss-otp-mode-history-modal',
    standalone: true,
    imports: [
        NgIf,
        NgForOf,
        DatePipe,
        SpinnerComponent
    ],
    templateUrl: './otp-mode-history.modal.html'
})
export class OtpModeHistoryModal implements OnInit {

    @Input() userId: number;
    /** Display-only context in the modal header; may arrive masked (PHI masking). */
    @Input() userName = '';

    loading = true;
    error = false;
    entries: OtpModeHistoryEntry[] = [];

    constructor(
        public modal: NgbActiveModal,
        private otpModeService: OtpModeService,
        private toastr: ToastrService,
    ) {
    }

    async ngOnInit() {
        await this.load();
    }

    /** "Static" / "Dynamic" — the two values {@link OtpModeEnum} carries. */
    modeLabel(mode: OtpModeEnum): string {
        return mode === OtpModeEnum.STATIC ? 'Static' : 'Dynamic';
    }

    private async load(): Promise<void> {
        this.loading = true;
        this.error = false;
        try {
            this.entries = await this.otpModeService.listHistory(this.userId);
        } catch (error: any) {
            // Backend owns the user-facing error text — surface it verbatim,
            // plus an inline message in the modal body.
            this.error = true;
            this.toastr.error(error?.error?.error);
        } finally {
            this.loading = false;
        }
    }
}
